import classNames from "classnames";
import { ChannelListFilter } from "components/ChannelSearch/ChannelListFilter";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { DividerLine } from "components/icons/DividerLine";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { ComponentProps, memo, useContext } from "react";
import { useToggleChannelRepliedForBulkMutation } from "store/apis/chat";
import { Button } from "components/DesignSystem/Button/Button";
import { SelectedChannelsContext } from "./SelectedChannelContext";
import { ChannelSearch } from "components/ChannelSearch/ChannelSearch";
import { ChatGroupFilters } from "./ChatGroupFilters";

const MemoizedChannelSearch = memo(ChannelSearch);

export const ChannelFilterOrSearch = ({
  hideFilters,
  unreplied,
  defaultNewestSort,
  isMyMessages,
  showEntityFilters,
}: {
  unreplied: boolean;
  defaultNewestSort?: boolean;
  isMyMessages?: boolean;
  showEntityFilters?: boolean;
} & Pick<ComponentProps<typeof ChannelListFilter>, "hideFilters">) => {
  const {
    setSelectedChannels,
    selectedChannels,
    availableChannels,
    channelNeedsReply,
  } = useContext(SelectedChannelsContext);
  const { isCustomer } = useRoleBasedView();
  const [toggleChannelRepliedForBulk, { isLoading }] =
    useToggleChannelRepliedForBulkMutation();
  const { successToast, alertToast } = useToast();

  const markSelectedChannels = async (needsReply: boolean) => {
    try {
      await toggleChannelRepliedForBulk({
        channelIds: selectedChannels,
        channelNeedsReply: needsReply,
      }).unwrap();

      successToast({
        message: `Channels marked as ${
          needsReply ? "unreplied" : "replied"
        } successfully`,
      });
      setSelectedChannels([]);
    } catch (error: any) {
      alertToast({
        message: error?.data?.error?.message || "Something went wrong",
      });
    }
  };

  const allSelected =
    availableChannels.length > 0 &&
    availableChannels.every((id) => selectedChannels.includes(id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedChannels([]);
      return;
    }
    setSelectedChannels(availableChannels);
  };

  const hasRepliedSelected = selectedChannels.some(
    (id) => channelNeedsReply[id] === false
  );
  const hasUnrepliedSelected = selectedChannels.some(
    (id) => channelNeedsReply[id] === true
  );
  const showMarkAsUnreplied = hasRepliedSelected && !hasUnrepliedSelected;
  const showGroupFilters = Boolean(showEntityFilters) && isCustomer;

  const searchRowClassName = classNames("t-flex t-items-center", {
    "t-gap-2": showGroupFilters,
    "t-gap-4": !showGroupFilters,
  });

  return (
    <div className="t-flex t-gap-4 t-flex-col">
      <div className={searchRowClassName}>
        <MemoizedChannelSearch
          hideFilters={hideFilters}
          defaultNewestSort={defaultNewestSort}
          unreplied={unreplied}
          isMyMessages={isMyMessages}
          hideUnreadChip={showEntityFilters}
        />
        {showGroupFilters && (
          <>
            <div className="t-h-6 t-w-px t-shrink-0 t-bg-neutral-10" />
            <ChatGroupFilters />
          </>
        )}
      </div>
      {selectedChannels.length > 0 && (
        <div className="t-flex t-gap-3 t-items-center">
          <Checkbox
            onChange={toggleSelectAll}
            checked={allSelected}
            indeterminate={!allSelected}
          />
          <span
            className="t-text-text-30 t-text-body-sm t-cursor-pointer"
            role="button"
            onClick={toggleSelectAll}
          >
            {selectedChannels.length} Selected
          </span>
          <span className="t-text-text-30 t-text-body-sm t-flex t-items-center">
            <DividerLine />{" "}
          </span>
          {showMarkAsUnreplied ? (
            <Button
              size="small"
              customType="primary_outlined"
              onClick={() => markSelectedChannels(true)}
              isLoading={isLoading}
              disabled={isLoading}
            >
              Mark as unreplied
            </Button>
          ) : (
            <Button
              size="small"
              customType="primary_outlined"
              onClick={() => markSelectedChannels(false)}
              isLoading={isLoading}
              disabled={isLoading}
            >
              Mark as replied
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
